/**
 * Khalid Al-Mansoori (Cardiometabolic Pathway GLP-1 Cardiometabolic) - PROD-579 fixture.
 *
 * Source-of-truth: HTML-PROTOTYPE-PARITY-AUDIT §A.11 + master Pre-flight
 * patient narrative + ORIGINAL-PROTOTYPE-PROMPTS Prompt 1 (MRN-004821, 42M,
 * Cardiometabolic Pathway Step 1). Verbatim NBA copy from HTML-BEHAVIORAL-EXTRACTION §3 Cardiometabolic Pathway
 * (em dashes rewritten per HC5; clinical content preserved).
 *
 * Tenant: apex-cardiometabolic. Future PROD-568 wires tenant filtering so
 * this profile is visible only when active tenant is Apex.
 *
 * Per HC1 + ADR-PROTO-002 mitigation 4: every value below is synthetic.
 * No real PII anywhere. The name is the ONE concession; the [SYNTHETIC]
 * suffix is rendered by PROD-569 PatientNameTag (placeholder span this
 * ticket).
 *
 * Per HC5: zero em dashes. Section separators use `-` (hyphen),
 * commas, periods, or "and" / "to" instead.
 */
import type { PatientFixture } from './types';

export const khalidAlMansoori: PatientFixture = {
  id: 'khalid-al-mansoori',
  name: 'Khalid Al-Mansoori',
  age: 42,
  sex: 'M',
  tenant: 'apex-cardiometabolic',
  pathway: 'cardiometabolic-pathway',
  presetClinicalQuestion:
    'Eligible for Cardiometabolic Pathway GLP-1 enrollment? Confirm baseline labs and dispatch MSQ.',
  summary:
    'BMI 31.2, hypertension and pre-diabetes. Cardiometabolic Pathway Step 1 enrollment review.',
  chart: {
    problemList: [
      'Obesity, class I (BMI 31.2)',
      'Essential hypertension',
      'Pre-diabetes (impaired fasting glucose)',
      'Mixed hyperlipidemia',
      'Mild obstructive sleep apnea (self-report)',
    ],
    medications: [
      'Lisinopril 10 mg PO daily',
      'Atorvastatin 20 mg PO nightly',
      'Aspirin 81 mg PO daily',
    ],
    vitals: {
      bmi: 31.2,
      heightCm: 178,
      weightKg: 98.9,
      bloodPressure: '138/86 mmHg',
      heartRate: 78,
      temperatureC: 36.7,
    },
    lastVisit: {
      date: '2026-03-21',
      summary:
        'Annual physical. BP up-trending. Counseled on weight, diet, and pre-DM. Repeat labs and consider Cardiometabolic Pathway enrollment.',
    },
  },
  labs: {
    HbA1c: {
      value: 6.3,
      unit: '%',
      referenceRange: '4.0 to 5.6',
      collectedAt: '2026-03-21',
    },
    LDL: {
      value: 132,
      unit: 'mg/dL',
      referenceRange: 'less than 100',
      collectedAt: '2026-03-21',
    },
    ApoB: {
      value: 104,
      unit: 'mg/dL',
      referenceRange: 'less than 90',
      collectedAt: '2026-03-21',
    },
    eGFR: {
      value: 92,
      unit: 'mL/min/1.73m2',
      referenceRange: 'greater than 60',
      collectedAt: '2026-03-21',
    },
    hsCRP: {
      value: 3.4,
      unit: 'mg/L',
      referenceRange: 'less than 1.0',
      collectedAt: '2026-03-21',
    },
    triglycerides: {
      value: 188,
      unit: 'mg/dL',
      referenceRange: 'less than 150',
      collectedAt: '2026-03-21',
    },
    HDL: {
      value: 38,
      unit: 'mg/dL',
      referenceRange: 'greater than 40',
      collectedAt: '2026-03-21',
    },
    fastingGlucose: {
      value: 108,
      unit: 'mg/dL',
      referenceRange: '70 to 99',
      collectedAt: '2026-03-21',
    },
    TSH: {
      value: 2.1,
      unit: 'mIU/L',
      referenceRange: '0.4 to 4.5',
      collectedAt: '2026-03-21',
    },
  },
  // ICD-10: obesity, essential HTN, pre-DM, mixed hyperlipidemia, OSA.
  icd10: ['E66.9', 'I10', 'R73.03', 'E78.5', 'G47.33'],
  nbas: [
    {
      id: 'khalid-tlp1-nba-1',
      title: 'Order baseline lab panel',
      rationale:
        'HbA1c, Lipid panel, CMP, TSH, eGFR required before Step 3 gate. Patient fasting confirmed.',
      confidence: 0.94,
      riskTier: 'manual_approve',
      actionType: 'order_lab',
      ctaVerb: 'Order',
      cited_sources: [
        {
          id: 'ADA-2024-Standards',
          title: 'ADA Standards of Care in Diabetes',
          year: 2024,
          url: 'https://diabetesjournals.org/care/issue/47/Supplement_1',
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Accepted 96% of the time at this clinic',
    },
    {
      id: 'khalid-tlp1-nba-2',
      title: 'Dispatch MSQ baseline PROM',
      rationale:
        '59-item metabolic questionnaire required at Cardiometabolic Pathway enrollment. Sent via JITAI trigger to patient app.',
      confidence: 0.91,
      riskTier: 'review_window',
      actionType: 'dispatch_PROM',
      ctaVerb: 'Dispatch',
      cited_sources: [
        {
          id: 'Scrips-PROM-Engine',
          title: 'Scrips PROM Engine instrument catalog (Cardiometabolic Pathway)',
          year: 2026,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Always accepted for Cardiometabolic Pathway Step 1',
    },
    {
      id: 'khalid-tlp1-nba-3',
      title: 'Present PathwayView now',
      rationale:
        'Show 1-year, 5-year, 10-year trajectory before enrollment decision. Increases 16-week commitment 3x.',
      confidence: 0.71,
      riskTier: 'manual_approve',
      // [IMPLEMENTED][PROD-583] Temporary classification: PathwayView is a
      // UX gesture, not yet registered in ADR-001 Layer 2 closed enum.
      // Classified as `send_educational_message` until Phase 2 ADR-001
      // amendment lands (see PROD-583 follow-up ticket).
      actionType: 'send_educational_message',
      ctaVerb: 'Present',
      cited_sources: [
        {
          id: 'CDS-Adoption-Lit-2024',
          title:
            'Pre-pilot CDS adoption literature benchmarks (illustrative projection)',
          year: 2024,
        },
      ],
      clinician_review_required: true,
      red_flags_detected: [],
      learn_signal: 'Shown in 68% of Cardiometabolic Pathway Step 1 visits',
    },
  ],
  ethnic_subgroup: 'arab_mena',
  ramadan_observant: true,
  soapDraft: {
    cc: 'Annual visit follow-up. Discuss Cardiometabolic Pathway enrollment.',
    hpi: '42M with class I obesity, hypertension, and pre-diabetes since 2024. BMI trending up over 12 months despite lifestyle counseling. No chest pain, dyspnea, or polyuria. Sleep partially refreshing; partner reports snoring. Motivated to enroll in a structured weight-management protocol.',
    assessment:
      'Cardiometabolic risk cluster (BMI 31.2, BP 138/86, HbA1c 6.3, LDL 132, ApoB 104, hsCRP 3.4). Eligible for Cardiometabolic Pathway GLP-1 protocol. No GLP-1 contraindications: no thyroid C-cell history, no pancreatitis, eGFR 92.',
    plan: 'Order baseline lab panel (HbA1c, lipid, CMP, TSH, eGFR). Dispatch MSQ baseline PROM. Schedule Cardiometabolic Pathway Step 2 visit at week 2 for results review. Counsel on Mediterranean diet pattern and 150 minutes per week of moderate activity.',
  },
};
